import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate, daysFromNow } from "../lib/api";
import Topbar from "../components/Topbar";
import PizzaSpinner from "../components/PizzaSpinner";
import EmptyState from "../components/EmptyState";
import { ChevronRight, Building2, AlertCircle, Calendar, Banknote } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AccountStatements() {
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/account-statements").then((r) => setList(r.data)).catch(() => {});
  }, []);

  if (!list) return <><Topbar title={t("statements.title")} /><PizzaSpinner /></>;

  const totalDebt = list.reduce((a, b) => a + b.total_unpaid, 0);
  const overdue = list.filter((s) => s.next_due && daysFromNow(s.next_due.date) < 0).length;

  return (
    <>
      <Topbar title={t("statements.title")} subtitle={t("statements.subtitle")} />
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] text-white rounded-2xl p-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFDF00]/10 rounded-full blur-2xl" />
            <div className="text-xs uppercase tracking-wider font-bold text-slate-300 relative">{t("statements.totalDebt")}</div>
            <div className="text-3xl font-display font-extrabold mt-2 relative">{fmtMXN(totalDebt)}</div>
            <div className="text-xs text-slate-400 mt-1 relative">{t("statements.providersCount", { count: list.filter(s => s.unpaid_count > 0).length })}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{t("statements.overdueInvoices")}</div>
            <div className="text-3xl font-display font-extrabold mt-2 text-red-600">{overdue}</div>
            <div className="text-xs text-slate-500 mt-1">{t("statements.overdueHint")}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-[#FFFBE6] border border-[#FFDF00] rounded-2xl p-5">
            <div className="text-xs uppercase tracking-wider font-bold text-[#1A1A1A]">{t("statements.activeSuppliers")}</div>
            <div className="text-3xl font-display font-extrabold mt-2">{list.length}</div>
            <div className="text-xs text-slate-700 mt-1">{t("statements.activeSuppliersHint")}</div>
          </motion.div>
        </div>

        {list.length === 0 ? (
          <EmptyState title={t("statements.emptyTitle")} message={t("statements.emptyMsg")} />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {list.map((s, idx) => {
              const dueDays = s.next_due ? daysFromNow(s.next_due.date) : null;
              const urgent = dueDays !== null && dueDays <= 3;
              return (
                <motion.div key={s.supplier_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(idx * 0.04, 0.4) }}
                  onClick={() => navigate(`/estado-cuenta/${s.supplier_id}`)}
                  className={`p-5 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 transition-all ${urgent ? "bg-red-50/50" : ""}`}
                  data-testid={`statement-card-${s.supplier_id}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                      urgent ? "bg-red-100" : s.total_unpaid > 0 ? "bg-amber-100" : "bg-emerald-100"
                    }`}>
                      <Building2 className={`w-5 h-5 ${
                        urgent ? "text-red-600" : s.total_unpaid > 0 ? "text-amber-700" : "text-emerald-700"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-[#1A1A1A] truncate">{s.supplier_name}</h3>
                        {urgent && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold uppercase">
                            <AlertCircle className="w-3 h-3" /> {t("statements.urgent")}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {t("statements.creditDays", { n: s.credit_days })}</span>
                        <span className="flex items-center gap-1"><Banknote className="w-3 h-3" /> {s.bank_name} ···{s.bank_account_last4}</span>
                        <span>{t("statements.pendingInvoices", { count: s.unpaid_count })}</span>
                        {s.pos_pending > 0 && <span className="text-amber-700 font-semibold">{t("statements.posPending", { count: s.pos_pending })}</span>}
                      </div>
                      {s.next_due && (
                        <div className="mt-2 text-xs">
                          <span className="text-slate-500">{t("statements.nextPayment")} </span>
                          <span className="font-mono font-bold">{fmtMXN(s.next_due.amount)}</span>
                          <span className={`ml-2 ${urgent ? "text-red-600 font-bold" : "text-slate-500"}`}>
                            ({dueDays < 0 ? t("statements.overdueByD", { n: Math.abs(dueDays) }) : t("statements.inDaysD", { n: dueDays })} · {fmtDate(s.next_due.date)})
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-right hidden sm:block">
                      <div className="text-2xl font-display font-extrabold font-mono text-[#1A1A1A]">{fmtMXN(s.total_unpaid)}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("statements.toPay")}</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
